/*
Expected data formats for different chart types:

PIE CHART:
{
  "type": "pie",
  "title": "Sample Pie Chart",
  "data": [
    { "name": "Category A", "value": 30 },
    { "name": "Category B", "value": 70 }
  ]
}

SCATTER CHART:
{
  "type": "scatter",
  "title": "Sample Scatter Chart",
  "data": [
    { "x": 10, "y": 20 },
    { "x": 15, "y": 25 },
    { "x": 20, "y": 30 }
  ]
}

WATERFALL CHART:
{
  "type": "waterfall",
  "title": "Sample Waterfall Chart",
  "data": [
    { "name": "Starting", "value": 100 },
    { "name": "Increase", "value": 20 },
    { "name": "Decrease", "value": -10 },
    { "name": "Final", "value": 110 }
  ]
}

LINE CHART:
{
  "type": "line",
  "title": "Sample Line Chart",
  "data": {
    "labels": ["Jan", "Feb", "Mar"],
    "datasets": [
      {
        "label": "Sales",
        "data": [100, 120, 90],
        "borderColor": "#8884d8"
      }
    ]
  }
}

BAR CHART:
{
  "type": "bar",
  "title": "Sample Bar Chart",
  "data": {
    "labels": ["A", "B", "C"],
    "datasets": [
      {
        "label": "Values",
        "data": [10, 20, 30],
        "backgroundColor": "#82ca9d"
      }
    ]
  }
}
*/

import React, { useState, useEffect } from 'react';
import {
    ResponsiveContainer, PieChart, Pie, Cell,
    BarChart, LineChart, ComposedChart, ScatterChart,
    Scatter, Bar, Line, CartesianGrid, XAxis, YAxis,
    Tooltip, Legend, ReferenceLine,
} from 'recharts';
import ChartContainer from './common/ChartContainer';

// Enhanced formatters
const formatters = {
    default: (value) => value,
    number: (value) => Number(value).toLocaleString('en-IN'),
    percentage: (value) => `${value}%`,
    currency: (value) => `₹${Number(value).toLocaleString('en-IN')}`,
};

// Predefined color palette for consistent chart colors - Updated to match user's image
const defaultColors = [
    '#FF6B9D', // Pink/Coral (Title 01)
    '#FF8F5A', // Orange (Title 02) 
    '#FFD93D', // Yellow (Title 03)
    '#6BCF7F', // Teal/Green (Title 04)
    '#4D96FF', // Purple/Blue (Title 05)
    '#9B59B6', // Purple (Title 06)
    '#E74C3C', // Red (additional)
    '#F39C12', // Amber (additional)
    '#2ECC71', // Green (additional)
    '#3498DB', // Blue (additional)
    '#9B59B6', // Purple (additional)
    '#1ABC9C', // Turquoise (additional)
    '#E67E22', // Orange (additional)
    '#34495E', // Dark blue-gray (additional)
    '#95A5A6', // Gray (additional)
];

// Get consistent color by index
// @ts-ignore - index is not used
const getChartColor = (index) => {
    return defaultColors[index % defaultColors.length];
};

// Get consistent "random" color based on data content
const getRandomConsistentColor = (dataKey) => {
    // Create a simple hash from the data key
    let hash = 0;
    for (let i = 0; i < dataKey.length; i++) {
        const char = dataKey.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Use hash to get a "random" but consistent index
    const colorIndex = Math.abs(hash) % defaultColors.length;
    return defaultColors[colorIndex];
};

// @ts-ignore - config is not used
const transformWaterfallData = (data, config) => {
    return data.map((item, index, arr) => {
        const start = index === 0 ? 0 : arr.slice(0, index).reduce((sum, val) => sum + val.value, 0);
        const isTotal = index === 0 || index === arr.length - 1;
        return {
            ...item,
            start,
            isTotal,
            isNegative: item.value < 0,
            itemColor: isTotal ? '#8884d8' : item.value < 0 ? '#EF4444' : '#82ca9d'
        };
    });
};

const CustomBar = ({
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    payload
}) => {
    const color = payload?.itemColor || '#8884d8';

    return (
        <rect
            x={x}
            y={height < 0 ? y + height : y}
            width={width}
            height={Math.abs(height)}
            fill={color}
        />
    );
};

const DynamicChart = ({ code, initialLoading = false }) => {
    const [chartConfig, setChartConfig] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Enhanced formatter function
    const getFormatter = (format) => (value) => {
        if (!format) return value;
        
        if (format === 'currency') {
            if (typeof value !== 'number') {
                return value;
            }
            
            // Format the number
            const formattedValue = Number(value).toLocaleString('en-IN');
            
            // Extract currency information from the chart configuration
            let currencySymbol = '';
            let currencySuffix = '';
            
            // Look for currency information in the y-axis title
            if (chartConfig?.yAxes?.[0]?.title?.text) {
                const yAxisTitle = chartConfig.yAxes[0].title.text;
                // Pattern like "Revenue (₹ Mn)"
                const match = yAxisTitle.match(/\(([^)]+)\)/);
                if (match) {
                    const parts = match[1].trim().split(' ');
                    if (parts[0] && /^[₹$€£¥]/.test(parts[0])) {
                        currencySymbol = parts[0];
                    }
                    if (parts.length > 1) {
                        currencySuffix = parts[1];
                    }
                }
            }
            
            // If not found in y-axis, check chart title
            if ((!currencySymbol || !currencySuffix) && chartConfig?.title) {
                const title = typeof chartConfig.title === 'string' ? 
                              chartConfig.title : 
                              chartConfig.title;
                
                if (title) {
                    const match = title.match(/\(([^)]+)\)/);
                    if (match) {
                        const parts = match[1].trim().split(' ');
                        if (!currencySymbol && parts[0] && /^[₹$€£¥]/.test(parts[0])) {
                            currencySymbol = parts[0];
                        }
                        if (!currencySuffix && parts.length > 1) {
                            currencySuffix = parts[1];
                        }
                    }
                }
            }
            
            // Apply formatting with the extracted information
            if (currencySymbol && currencySuffix) {
                return `${currencySymbol}${formattedValue} ${currencySuffix}`;
            } else if (currencySymbol) {
                return `${currencySymbol}${formattedValue}`;
            } else if (currencySuffix) {
                return `${formattedValue} ${currencySuffix}`;
            }
            
            return formattedValue;
        } 
        else if (format === 'number') {
            return formatters.number(value);
        }
        else if (format === 'percentage') {
            return formatters.percentage(value);
        }
        else {
            // Try to use a named formatter if it exists
            const formatter = formatters[format] || formatters.default;
            return formatter(value);
        }
    };

    useEffect(() => {
        if (initialLoading) {
            setIsLoading(true);
            setError(null);
            return;
        }

        if (!code || (typeof code === 'string' && code.trim() === '')) {
            setIsLoading(true);
            setError(null);
            return;
        }

        try {
            const parsedConfig = typeof code === 'string' ? JSON.parse(code) : code;
            // console.log('Parsed chart config:', parsedConfig);

            // Basic config validation
            if (!parsedConfig || !parsedConfig.type || !parsedConfig.data) {
                // During streaming, incomplete data should show loading, not error
                setIsLoading(true);
                setError(null);
                return;
            }

            // Validate based on chart type
            switch (parsedConfig.type) {
                case 'pie':
                    if (!Array.isArray(parsedConfig.data)) {
                        // Check if it's using the datasets format (Chart.js style)
                        if (parsedConfig.data?.labels && parsedConfig.data?.datasets && 
                            Array.isArray(parsedConfig.data.labels) && 
                            Array.isArray(parsedConfig.data.datasets)) {
                            // Validate datasets format
                            const hasValidDatasets = parsedConfig.data.datasets.every(ds => 
                                ds.data && Array.isArray(ds.data) && ds.data.length > 0
                            );
                            if (!hasValidDatasets || parsedConfig.data.labels.length === 0) {
                                setIsLoading(true);
                                setError(null);
                                return;
                            }
                        } else {
                        // During streaming, this might be incomplete - show loading
                        setIsLoading(true);
                        setError(null);
                        return;
                    }
                    } else {
                        // Original simple format validation
                    if (parsedConfig.data.length === 0) {
                        // Empty array during streaming - show loading
                        setIsLoading(true);
                        setError(null);
                        return;
                        }
                    }
                    break;
                
                case 'waterfall':
                    if (!Array.isArray(parsedConfig.data)) {
                        setIsLoading(true);
                        setError(null);
                        return;
                    }
                    break;
                    
                case 'scatter':
                    if (!Array.isArray(parsedConfig.data)) {
                        // Check if it's using the datasets format
                        if (parsedConfig.data?.datasets && Array.isArray(parsedConfig.data.datasets)) {
                            // Validate datasets format
                            const hasValidDatasets = parsedConfig.data.datasets.every(ds => 
                                ds.data && Array.isArray(ds.data) && 
                                ds.data.every(item => 
                                    typeof item === 'object' && 
                                    item.hasOwnProperty('x') && 
                                    item.hasOwnProperty('y')
                                )
                            );
                            if (!hasValidDatasets) {
                                setIsLoading(true);
                                setError(null);
                                return;
                            }
                        } else {
                        setIsLoading(true);
                        setError(null);
                        return;
                    }
                    } else {
                        // Original simple format validation
                    const hasScatterStructure = parsedConfig.data.every((item) => 
                        typeof item === 'object' && 
                        item.hasOwnProperty('x') && 
                        item.hasOwnProperty('y')
                    );
                    if (!hasScatterStructure) {
                        // During streaming, incomplete structure should show loading
                        setIsLoading(true);
                        setError(null);
                        return;
                        }
                    }
                    break;

                case 'line':
                case 'bar':
                case 'composed':
                    if (!parsedConfig.data.datasets || !Array.isArray(parsedConfig.data.datasets)) {
                        setIsLoading(true);
                        setError(null);
                        return;
                    }
                    if (!parsedConfig.data.labels || !Array.isArray(parsedConfig.data.labels)) {
                        setIsLoading(true);
                        setError(null);
                        return;
                    }
                    break;

                default:
                    // Unknown chart type during streaming - show loading
                    setIsLoading(true);
                    setError(null);
                    return;
            }

            // If we reach here, we have valid chart config
            setChartConfig(parsedConfig);
            setError(null);
            setIsLoading(false);
            
        } catch (err) {
            // During streaming, most errors are due to incomplete JSON
            // Only show actual errors after a delay to avoid flickering during streaming
            console.log('Chart parsing issue (likely streaming):', err.message);
            
            if (err instanceof SyntaxError) {
                // JSON parsing error - definitely streaming
                setIsLoading(true);
                setError(null);
            } else {
                // Other validation errors during streaming should also show loading
                // Only show as error if it persists (indicating actual problem)
                setIsLoading(true);
                setError(null);
                
                // Optional: Set a timeout to show error if it persists
                // This prevents showing errors during normal streaming
                setTimeout(() => {
                    // Re-check if we still have the same error after streaming should be done
                    try {
                        const recheckConfig = typeof code === 'string' ? JSON.parse(code) : code;
                        if (!recheckConfig || !recheckConfig.type || !recheckConfig.data) {
                            // Still invalid after delay - show error
                            setError('Invalid chart configuration');
                            setIsLoading(false);
                        }
                    } catch {
                        // Still parsing error after delay - might be actual issue
                        setError('Failed to parse chart data');
                        setIsLoading(false);
                    }
                }, 2000); // Wait 2 seconds before showing persistent errors
            }
        }
    }, [code, initialLoading]);

    if (initialLoading || isLoading) {
        return (
            <ChartContainer title="Loading Chart">
                <div className="flex items-center justify-center h-96">
                    <div className="text-center text-gray-400">
                        <div className="w-12 h-12 border-4 border-t-blue-500 border-gray-600 rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-sm">Loading chart data...</p>
                        <p className="text-xs text-gray-500 mt-1">Processing visualization</p>
                    </div>
                </div>
            </ChartContainer>
        );
    }

    if (error) {
        return (
            <div className="p-4 bg-red-900/50 text-red-200 rounded-lg">
                <p>Failed to load chart: {error}</p>
            </div>
        );
    }

    if (!chartConfig) {
        return (
            <div className="p-4 bg-gray-800 text-gray-400 rounded-lg">
                <p>No chart data available</p>
            </div>
        );
    }

    // Handle Pie Charts
    if (chartConfig.type === 'pie') {
        let normalizedData = [];
        let colors = [];

        if (Array.isArray(chartConfig.data)) {
            // Simple format: data is directly an array
            normalizedData = chartConfig.data.map((item) => ({
            name: item.name || item.label || 'Unknown',
            value: item.value || item.y || 0,
            percentage: item.percentage
        }));
            colors = chartConfig.colors || [];
        } else if (chartConfig.data?.labels && chartConfig.data?.datasets) {
            // Datasets format: data.labels and data.datasets[].data
            const labels = chartConfig.data.labels;
            const dataset = chartConfig.data.datasets[0]; // Use first dataset for pie chart
            const values = dataset.data;
            const backgroundColor = dataset.backgroundColor || [];

            normalizedData = labels.map((label, index) => ({
                name: label,
                value: values[index] || 0,
                percentage: null // Could calculate if needed
            }));
            colors = backgroundColor;
        }

        return (
            <ChartContainer title={chartConfig.title}>
                <ResponsiveContainer width="100%" height={450}>
                    <PieChart>
                        <Pie
                            data={normalizedData}
                            dataKey="value"
                            nameKey="name"
                            cx="35%"
                            cy="50%"
                            outerRadius="60%"
                            innerRadius={0}
                            label={false}
                            labelLine={false}
                            fill="#8884d8"
                        >
                            {normalizedData.map((entry, index) => (
                                <Cell 
                                    key={`cell-${index}`}
                                    fill={colors[index] || getRandomConsistentColor(entry.name)}
                                />
                            ))}
                        </Pie>
                        <Tooltip 
                            formatter={(value, name, props) => {
                                const payload = props?.payload;
                                if (!payload) return [getFormatter(chartConfig.format)(value), name];
                                
                                const entry = normalizedData[payload.index];
                                if (!entry) return [getFormatter(chartConfig.format)(value), name];

                                const formattedValue = getFormatter(chartConfig.format)(value);
                                const displayValue = entry.percentage != null 
                                    ? `${formattedValue} (${entry.percentage}%)`
                                    : formattedValue;

                                return [displayValue, entry.name || name];
                            }}
                            contentStyle={{
                                backgroundColor: '#1F2937',
                                border: '1px solid #4B5563',
                                color: '#E5E7EB'
                            }}
                            labelStyle={{ color: '#E5E7EB' }}
                            itemStyle={{ color: '#E5E7EB' }}
                        />
                        <Legend 
                            verticalAlign="middle" 
                            align="right"
                            layout="vertical"
                            iconSize={14}
                            wrapperStyle={{ 
                                color: '#E5E7EB',
                                fontSize: '13px',
                                paddingLeft: '15px',
                                width: '35%',
                                overflow: 'visible'
                            }}
                            formatter={(value) => {
                                // Truncate long legend labels to prevent overflow
                                return value.length > 20 ? `${value.substring(0, 20)}...` : value;
                            }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </ChartContainer>  
        );
    }

    // Handle Scatter Plots - FIXED: Remove data prop from ScatterChart
    if (chartConfig.type === 'scatter') {
        // Determine data format and prepare scatter data
        let scatterData = [];
        let datasets = [];

        if (Array.isArray(chartConfig.data)) {
            // Simple format: data is directly an array
            scatterData = chartConfig.data;
            datasets = [{ label: 'Data', data: chartConfig.data, color: '#8884d8' }];
        } else if (chartConfig.data?.datasets) {
            // Datasets format: data.datasets[].data
            datasets = chartConfig.data.datasets.map((ds, index) => ({
                label: ds.label || `Dataset ${index + 1}`,
                data: ds.data,
                color: ds.backgroundColor?.[0] || ds.backgroundColor || getRandomConsistentColor(ds.label || `dataset_${index}`)
            }));
            // Flatten all datasets for the overall chart
            scatterData = datasets.flatMap(ds => ds.data);
        }

        return (
            <ChartContainer title={chartConfig.title}>
                <ResponsiveContainer width="100%" height={450}>
                    <ScatterChart margin={{ top: 20, right: 30, bottom: 60, left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
                        <XAxis
                            type="number"
                            dataKey="x"
                            name={chartConfig.xAxis?.title?.text || chartConfig.xAxis?.label || 'X'}
                            tickFormatter={getFormatter(chartConfig.xAxis?.format)}
                            tick={{ fill: '#E5E7EB', fontSize: 11 }}
                        />
                        <YAxis
                            type="number"
                            dataKey="y"
                            name={chartConfig.yAxis?.title?.text || chartConfig.yAxis?.label || chartConfig.yAxes?.[0]?.label || 'Y'}
                            tickFormatter={getFormatter(chartConfig.yAxis?.format)}
                            tick={{ fill: '#E5E7EB', fontSize: 11 }}
                        />
                        <Tooltip 
                            formatter={(value, name, props) => {
                                const payload = props?.payload;
                                // Show the label if available, otherwise use the value
                                const label = payload?.label || payload?.name || name;
                                return [
                                getFormatter(chartConfig.format)(value),
                                    label
                                ];
                            }}
                            labelFormatter={(label) => {
                                // For scatter plots, we can show the point label
                                return label || 'Data Point';
                            }}
                            contentStyle={{
                                backgroundColor: '#1F2937',
                                border: '1px solid #4B5563',
                                color: '#E5E7EB'
                            }}
                            labelStyle={{ color: '#E5E7EB' }}
                            itemStyle={{ color: '#E5E7EB' }}
                        />
                        {datasets.length > 1 && <Legend wrapperStyle={{ color: '#E5E7EB' }} />}
                        {datasets.map((dataset, index) => (
                        <Scatter
                                key={index}
                                name={dataset.label}
                                data={dataset.data}
                                fill={dataset.color}
                        />
                        ))}
                    </ScatterChart>
                </ResponsiveContainer>
            </ChartContainer>  
        );
    }

    // Handle Waterfall Charts
    if (chartConfig.type === 'waterfall') {
        // @ts-ignore - index is not used
        const waterfallData = transformWaterfallData(chartConfig.data, chartConfig).map((item, index) => ({
            ...item,
            itemColor: item.isTotal ? getRandomConsistentColor(`total_${item.name}`) : 
                      (item.isNegative ? getRandomConsistentColor(`negative_${item.name}`) : 
                       getRandomConsistentColor(`positive_${item.name}`))
        }));

        return (
            <ChartContainer title={chartConfig.title}>
                <ResponsiveContainer width="100%" height={450}>
                    <ComposedChart data={waterfallData} margin={{ top: 20, right: 30, bottom: 60, left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
                        <XAxis 
                            dataKey="name" 
                            tick={{ fill: '#E5E7EB', fontSize: 11 }}
                            angle={-25}
                            textAnchor="end"
                            height={60}
                            interval={0}
                        />
                        <YAxis tick={{ fill: '#E5E7EB', fontSize: 11 }} />
                        <Tooltip 
                            formatter={(value) => [getFormatter(chartConfig.format)(value), 'Value']}
                            contentStyle={{
                                backgroundColor: '#1F2937',
                                border: '1px solid #4B5563',
                                color: '#E5E7EB'
                            }}
                            labelStyle={{ color: '#E5E7EB' }}
                            itemStyle={{ color: '#E5E7EB' }}
                        />
                        <ReferenceLine y={0} stroke="#000" />
                        <Bar dataKey="start" fill="transparent" stackId="stack" />
                        <Bar dataKey="value" stackId="stack" shape={<CustomBar />} />
                    </ComposedChart>
                </ResponsiveContainer>
            </ChartContainer>
        );
    }

    // Handle Bar, Line, and Composed Charts - FIXED: Line chart component selection
    const flatData = chartConfig.data.labels.map((label, idx) => {
        const obj = { name: label };
        chartConfig.data.datasets.forEach((ds) => {
            obj[ds.label] = ds.data[idx];
        });
        return obj;
    });

    const isComposed = chartConfig.type === 'composed';
    const ChartTag = isComposed ? ComposedChart : chartConfig.type === 'bar' ? BarChart : LineChart;

    return (
        <ChartContainer title={chartConfig.title}>
            <ResponsiveContainer width="100%" height={450}>
                <ChartTag data={flatData} margin={{ top: 20, right: 30, bottom: 60, left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
                    <XAxis 
                        dataKey="name" 
                        tick={{ fill: '#E5E7EB', fontSize: 11 }}
                        angle={-25}
                        textAnchor="end"
                        height={60}
                        interval={0}
                    />
                    <YAxis tick={{ fill: '#E5E7EB', fontSize: 11 }} />
                    <Tooltip 
                        formatter={(value, name) => [getFormatter(chartConfig.format)(value), name]}
                        contentStyle={{
                            backgroundColor: '#1F2937',
                            border: '1px solid #4B5563',
                            color: '#E5E7EB'
                        }}
                        labelStyle={{ color: '#E5E7EB' }}
                        itemStyle={{ color: '#E5E7EB' }}
                    />
                    <Legend wrapperStyle={{ color: '#E5E7EB' }} />
                    {chartConfig.data.datasets.map((ds, i) => {
                        // FIXED: Proper component selection for line charts
                        const Component = (isComposed && ds.type === 'line') || chartConfig.type === 'line' ? Line : Bar;
                        return (
                            <Component
                                key={i}
                                dataKey={ds.label}
                                fill={ds.backgroundColor || getRandomConsistentColor(ds.label)}
                                stroke={ds.borderColor || getRandomConsistentColor(ds.label)}
                                type={(ds.type === 'line' || chartConfig.type === 'line') ? 'monotone' : undefined}
                                strokeWidth={chartConfig.type === 'line' ? 2 : undefined}
                            />
                        );
                    })}
                </ChartTag>
            </ResponsiveContainer>
        </ChartContainer>
    );
};

export default DynamicChart;
